<template>
  <d-motion-sticky
    ref="sticky"
    :data-selected="selected"
    :element="window"
    class="c-questionnaire"
  >
    <template v-slot:default="{ className }">
      <div class="c-questionnaire__top" :class="{'is-banner': isBanner}">
        <div class="c-questionnaire__black">
          <c-button
            :text="textBack"
            icon="@arrow-left"
            appearance="text"
            palette="tertiary"
            size="small"
            adaptive="auto"
            @click="onBack"
          />
        </div>
        <a href="/" class="c-questionnaire__logo a-static">
          <d-icon-item icon="@logo"/>
        </a>
        <div :class="{'is-banner': isBanner}" class="c-questionnaire__top-end"/>
      </div>
      <c-axis
        ref="axis"
        :selected="selected"
        class="c-questionnaire__question"
      >
        <template
          v-for="item in propQuestions"
          :key="item.index"
          v-slot:[item.index]
        >
          <c-questionnaire-banner
            v-if="item?.type === 'banner'"
            v-bind="item"
            @on-click="onNext"
          />
          <c-questionnaire-question v-else v-bind="item" class="c-questionnaire__question__item" @on-input="onNext"/>
        </template>
        <template v-slot:end>
          <div v-if="end" class="c-questionnaire__end">
            <slot :className="className"/>
          </div>
        </template>
      </c-axis>
      <div
        v-if="!isBanner && !end"
        :class="className"
        class="c-questionnaire__progress"
      >
        <c-execution-progress
          :value="counter"
          :max="length"
          class="c-questionnaire__progress__item"
        />
      </div>
    </template>
  </d-motion-sticky>
</template>

<script>
import CAxis from './CAxis'
import CButton from './CButton'
import CExecutionProgress from './CExecutionProgress'
import CQuestionnaireBanner from './CQuestionnaireBanner'
import CQuestionnaireQuestion from './CQuestionnaireQuestion'
import DIconItem from 'dcode/components/DIconItem'
import DMotionSticky from 'dcode/components/DMotionSticky'
import { computed, nextTick, ref } from 'vue'
import forEach from 'dcode/functions/forEach'
import useWatch from 'dcode/uses/useWatch'
import isSelected from 'dcode/functions/isSelected'

export default {
  name: 'CQuestionnaire',
  components: {
    CAxis,
    CButton,
    CExecutionProgress,
    CQuestionnaireBanner,
    CQuestionnaireQuestion,
    DIconItem,
    DMotionSticky
  },
  emits: [
    'on-input',
    'on-change'
  ],
  props: {
    // Values
    questions: [Array, Object],

    // Options
    logo: Boolean,
    back: Boolean,
    progress: Boolean,
    toHome: String,

    // Text
    textBack: {
      type: String,
      default: 'Back'
    }
  },
  setup (props, context) {
    const sticky = ref(undefined)
    const axis = ref(undefined)
    const end = ref(undefined)
    const values = ref({})
    const propQuestions = computed(() => {
      return forEach(props.questions, (item, index) => {
        return {
          ...item,
          index: `item-${item.name || index}`,
          item
        }
      })
    })

    const selected = useWatch(propQuestions, data => {
      values.value = {}
      data.value = propQuestions.value?.[0]?.index
    })
    const counter = useWatch([selected, propQuestions], data => {
      let counter = 0

      for (const item of propQuestions.value) {
        if (item?.type !== 'banner') {
          counter++
        }

        if (item.index === selected.value) {
          break
        }
      }

      data.value = counter
    })
    const length = useWatch(propQuestions, data => {
      data.value = propQuestions.value.filter(item => item?.type !== 'banner').length
    })

    const isBanner = useWatch([selected, propQuestions], data => {
      data.value = propQuestions.value.find(item => item.index === selected.value)?.type === 'banner'
    })

    const getItem = name => propQuestions.value.find(item => item.name === name)
    const getQuestionsValue = () => {
      const data = []
      forEach(values.value, (value, index) => {
        const item = getItem(index)

        const question = {
          ...item,
          answer: item?.answer?.filter(answer => isSelected(answer?.value, value)),
          answerValue: value
        }

        data.push(question)
      })

      return data
    }

    const toNext = (name, value = undefined) => {
      let data, index

      for (const item of propQuestions.value) {
        if (data) {
          if (
            !(('isShow' in item) && !item.isShow(values.value))
          ) {
            const next = data?.next ? data.next(value) : undefined

            index = next === undefined ? item.index : getItem(next)?.index
            axis.value.toRight(index)
            break
          }
        } else if (name === item.name) {
          data = item
        }
      }

      if (index) {
        selected.value = index

        return false
      } else {
        end.value = true
        axis.value.toUp('end')
        selected.value = 'end'

        nextTick().then(() => {
          setTimeout(() => sticky.value.update(), 480)
        })

        return true
      }
    }
    const toBack = () => {
      let index

      for (const item of propQuestions.value) {
        if (item.index === selected.value) {
          break
        } else if (
          !(('isShow' in item) && !item.isShow(values.value))
        ) {
          index = item.index
        }
      }

      if (index) {
        axis.value.toLeft(index)
        selected.value = index
      } else {
        location.href = props.toHome
      }
    }

    return {
      sticky,
      axis,
      end,

      propQuestions,
      selected,

      counter,
      length,

      isBanner,
      window: window,

      onNext (event) {
        values.value[event.name] = event?.value

        const end = toNext(event.name, event?.value)

        context.emit('on-input', {
          values: values.value,
          questions: getQuestionsValue()
        })

        if (end) {
          context.emit('on-change', {
            values: values.value,
            questions: getQuestionsValue()
          })
        }
      },
      onBack () {
        toBack()
      }
    }
  }
}
</script>

<style lang="scss">
@import "~dcode/styles/flex";
@import "~dcode/styles/margin";
@import "~dcode/styles/media";
@import "~dcode/styles/padding";
@import "~dcode/styles/position";

.c-questionnaire {
  --_qs-padding: var(--qs-padding, 16px);
  --_qs-padding-ds: var(--qs-padding-ds, 32px);
  --_qs-padding-ex: var(--qs-padding-ex, 64px);
  --_qs-pr-width: var(--qs-pr-width, 100%);
  --_qs-pr-width-ds: var(--qs-pr-width-ds, 468px);
  --_qs-pr-width-ex: var(--qs-pr-width-ex, 600px);
  --_qs-pr-padding-bottom: var(--qs-pr-padding-bottom, 16px);
  --_qs-pr-padding-bottom-md: var(--qs-pr-padding-bottom-md, 32px);
  --_qs-pr-padding-bottom-ds: var(--qs-pr-padding-bottom-ds, 40px);
  --_qs-pt-padding-top: var(--qs-pt-padding-top, 16px);
  --_qs-pt-padding-top-md: var(--qs-pt-padding-top-md, 32px);
  --_qs-pt-padding-top-ds: var(--qs-pt-padding-top-ds, 40px);
  --_qs-pt-padding-left: var(--qs-pt-padding-left, 16px);
  --_qs-pt-padding-left-md: var(--qs-pt-padding-left-md, 32px);
  --_qs-pt-padding-right: var(--qs-pt-padding-right, 16px);
  --_qs-pt-padding-right-md: var(--qs-pt-padding-right-md, 32px);
  --_qs-pt-padding-right-ds: var(--qs-pt-padding-right-ds, 40px);
  --_qs-pt-padding-right-ex: var(--qs-pt-padding-right-ex, 108px);
  --_qs-lg-width: var(--qs-lg-width, 135px);
  --_qs-lg-width-ex: var(--qs-lg-width-ex, 180px);
  --_qs-lg-height: var(--qs-lg-height, 24px);
  --_qs-lg-height-ex: var(--qs-lg-height-ex, 30px);

  position: relative;

  &__question__item {
    @include marginX(var(--_qs-padding));
    padding-bottom: 32px;

    @include mediaMinWidth(large) {
      padding-bottom: 78px;
    }
  }

  &__progress {
    @include marginX(var(--_qs-padding));
    padding-bottom: var(--_qs-pr-padding-bottom);
    z-index: 8;
    position: sticky;
    bottom: 0;

    &__item {
      margin: 0 auto;
      max-width: var(--_qs-pr-width);
    }

    @include mediaMinWidth() {
      position: absolute;
      left: 0;
      right: 0;
    }

    @include mediaMinWidth(large) {
      @include absoluteBottom;
    }
  }

  &__top {
    @include absoluteX();
    @include paddingDirectionX(var(--_qs-pt-padding-left), var(--_qs-pt-padding-left));
    display: flex;
    align-items: center;
    z-index: 4;
    padding-top: var(--_qs-pt-padding-top);

    &.is-banner {
      @include paddingDirectionX(var(--_qs-pt-padding-left), var(--_qs-pt-padding-right));
    }
  }

  &__black,
  &__top-end {
    flex-basis: 1px;
    flex-grow: 1;
  }

  &__black {
  }

  &__logo {
    display: block;
    position: relative;
    width: var(--_qs-lg-width);
    height: var(--_qs-lg-height);
  }

  @include mediaMinWidth(medium) {
    --_qs-padding: var(--_qs-padding-ds);
    --_qs-pr-padding-bottom: var(--_qs-pr-padding-bottom-md);
    --_qs-pt-padding-top: var(--_qs-pt-padding-top-md);
    --_qs-pt-padding-left: var(--_qs-pt-padding-left-md);
    --_qs-pt-padding-right: var(--_qs-pt-padding-right-md);

    &__question__item {
      padding-bottom: 0;
    }
  }

  @include mediaMinWidth(large) {
    --_qs-pr-width: var(--_qs-pr-width-ds);
    --_qs-pr-padding-bottom: var(--_qs-pr-padding-bottom-ds);
    --_qs-pt-padding-top: var(--_qs-pt-padding-top-ds);
    --_qs-pt-padding-left: var(--_qs-pt-padding-right-ds);
    --_qs-pt-padding-right: var(--_qs-pt-padding-right-ds);
    --_qs-lg-height: var(--_qs-lg-height-ex);
    --_qs-lg-width: var(--_qs-lg-width-ex);

    &__top-end {
      &.is-banner {
        flex-basis: 0;
        flex-grow: 0;
      }
    }

    & > .d-motion-axis,
    & > .d-motion-axis > .d-motion-axis__slide {
      @include absoluteFull;
    }
  }

  @include mediaMinWidth(extra) {
    --_qs-padding: var(--_qs-padding-ex);
    --_qs-pr-width: var(--_qs-pr-width-ex);
    --_qs-pt-padding-left: var(--_qs-pt-padding-right-ex);
    --_qs-pt-padding-right: var(--_qs-pt-padding-right-ex);
  }
}
</style>
